import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Search, CheckCircle, Archive, Reply, Clock } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import RowActions from '../../components/RowActions';
import { apiService } from '../../services/apiService';
import { formatDate, renderCell, renderCellDate } from '../../utils/helpers';
import { useToast } from '../../contexts/ToastContext';

const STATUS_LABELS = {
  pending: 'En attente',
  read: 'Lu',
  replied: 'Répondu',
  archived: 'Archivé',
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  read: 'bg-blue-100 text-blue-800',
  replied: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-600',
};

const ContactMessages = ({ embedded = false }) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadSubmissions();
  }, [statusFilter]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getContactSubmissions(statusFilter || null);
      setSubmissions(data.submissions || []);
    } catch (error) {
      console.error('Failed to load contact messages:', error);
      toastError('Impossible de charger les messages');
    } finally {
      setLoading(false);
    }
  };

  const filtered = submissions.filter((s) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.subject?.toLowerCase().includes(q) ||
      s.message?.toLowerCase().includes(q)
    );
  });

  const pendingCount = submissions.filter((s) => s.status === 'pending').length;

  const openDetail = (submission) => {
    setSelected(submission);
    setNotes(submission.notes || '');
    setDetailModal(true);
    if (submission.status === 'pending') {
      handleStatusUpdate(submission.id, 'read', submission.notes || '');
    }
  };

  const handleStatusUpdate = async (id, status, noteText = notes) => {
    try {
      setUpdating(true);
      await apiService.updateContactSubmission(id, status, noteText);
      setSubmissions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status, notes: noteText } : s))
      );
      if (selected?.id === id) {
        setSelected((prev) => ({ ...prev, status, notes: noteText }));
      }
      toastSuccess('Statut mis à jour');
    } catch (error) {
      toastError('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteContactSubmission(deleteConfirm.id);
      setSubmissions((prev) => prev.filter((s) => s.id !== deleteConfirm.id));
      setDeleteConfirm({ open: false, id: null, name: '' });
      if (selected?.id === deleteConfirm.id) {
        setDetailModal(false);
        setSelected(null);
      }
      toastSuccess('Message supprimé');
    } catch (error) {
      toastError('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600 mt-1">
              Demandes reçues depuis le site web et le portail client
            </p>
          </div>
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {pendingCount} message{pendingCount > 1 ? 's' : ''} en attente
              </span>
            </div>
          )}
        </div>
      )}
      {embedded && pendingCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg w-fit">
          <Clock className="w-5 h-5 text-amber-600" />
          <span className="text-sm font-medium text-amber-800">
            {pendingCount} message{pendingCount > 1 ? 's' : ''} en attente
          </span>
        </div>
      )}

      <Card>
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1">
            <Input
              icon={Search}
              placeholder="Rechercher nom, email, sujet..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="sm:w-48"
            placeholder="Tous les statuts"
            options={[
              { value: 'pending', label: 'En attente' },
              { value: 'read', label: 'Lu' },
              { value: 'replied', label: 'Répondu' },
              { value: 'archived', label: 'Archivé' },
            ]}
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun message pour le moment</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Expéditeur</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sujet</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase sticky right-0 z-20 bg-gray-50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((submission, index) => (
                  <motion.tr
                    key={submission.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className={`group hover:bg-gray-50 ${submission.status === 'pending' ? 'bg-amber-50/50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{renderCell(submission.name)}</div>
                      <div className="text-sm text-gray-500">{renderCell(submission.email)}</div>
                      <div className="text-xs text-gray-400">{renderCell(submission.phone)}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                      {submission.subject || 'Contact'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {renderCellDate(submission.createdAt, 'dd/MM/yyyy HH:mm')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[submission.status] || STATUS_COLORS.pending}`}>
                        {STATUS_LABELS[submission.status] || submission.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.12)]">
                      <RowActions
                        onView={() => openDetail(submission)}
                        onEdit={() => openDetail(submission)}
                        onDelete={() =>
                          setDeleteConfirm({
                            open: true,
                            id: submission.id,
                            name: submission.name,
                          })
                        }
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={detailModal}
        onClose={() => setDetailModal(false)}
        title="Message reçu"
        size="lg"
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500">Nom</label>
                <p className="text-gray-900 font-medium">{selected.name}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Email</label>
                <p className="text-gray-900">
                  <a href={`mailto:${selected.email}`} className="text-red-600 hover:underline">
                    {selected.email}
                  </a>
                </p>
              </div>
              {selected.phone && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Téléphone</label>
                  <p className="text-gray-900">{selected.phone}</p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500">Date</label>
                <p className="text-gray-900">{formatDate(selected.createdAt, 'dd/MM/yyyy HH:mm')}</p>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">Sujet</label>
              <p className="text-gray-900 font-medium">{selected.subject || 'Contact'}</p>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">Message</label>
              <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-800 whitespace-pre-wrap text-sm leading-relaxed">
                {selected.message}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500">Notes internes</label>
              <textarea
                className="mt-1 w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes de suivi..."
              />
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                icon={CheckCircle}
                loading={updating}
                onClick={() => handleStatusUpdate(selected.id, 'read')}
              >
                Marquer lu
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon={Reply}
                loading={updating}
                onClick={() => handleStatusUpdate(selected.id, 'replied')}
              >
                Marquer répondu
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={Archive}
                loading={updating}
                onClick={() => handleStatusUpdate(selected.id, 'archived')}
              >
                Archiver
              </Button>
              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || 'Votre message PETROFEU')}`}
                className="ml-auto"
              >
                <Button variant="primary" size="sm" icon={Mail}>
                  Répondre par email
                </Button>
              </a>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={handleDelete}
        title="Supprimer le message"
        message={`Supprimer le message de "${deleteConfirm.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        variant="danger"
      />
    </div>
  );
};

export default ContactMessages;
